import React, { useState, useRef, useEffect } from 'react';
import Chart from "react-apexcharts";
import { animated, useSpring } from '@react-spring/web';
import config from 'config.js';
import { Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { getCurrencySymbolV2, getCurrencySymbolV3 } from 'utils/currency';
import { FaSpinner } from 'react-icons/fa';

// ✅ Clear
function generateMonths(startDate, endDate) {
	const months = [];
	const date = new Date(startDate);
	const currentDate = new Date();

	currentDate.setDate(1);

	while (date <= endDate) {
		const monthYear = date.toLocaleString('default', {
			month: 'short',
			year: 'numeric',
		});

		const iteratingDate = new Date(date);
		iteratingDate.setDate(1);

		const isDisabled = iteratingDate > currentDate;

		months.push({
			label: monthYear,
			date: new Date(date),
			disabled: isDisabled,
		});
		date.setMonth(date.getMonth() + 1);
	}

	return months;
}

function generateDays(startDate, endDate) {
    const days = [];
    const date = new Date(startDate);

    while (date <= endDate) {
        days.push({
            label: date.toLocaleDateString('default', { month: 'short', day: 'numeric' }),
            date: new Date(date),
        });
        date.setDate(date.getDate() + 1);
    }

    return days;
}

function generateWeeks(startDate, endDate) {
    const weeks = [];
    const date = new Date(startDate);

    while (date <= endDate) {
        const weekStart = new Date(date);
        const weekEnd = new Date(date);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const label = `${weekStart.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`;
        weeks.push({ label, date: new Date(weekStart) });

        date.setDate(date.getDate() + 7);
    }

    return weeks;
}

function generateYears(startYear, endYear) {
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
        years.push({
            label: `${year}`,
            date: new Date(`${year}-01-01`)
        });
    }
    return years;
}

const formatDateLabel = (baseDate, index, intervalType) => {
    const date = new Date(baseDate);

    switch (intervalType) {
        case "daily":
            const dailyDate = new Date(date);
            const hour = index;
            dailyDate.setHours(hour);
            const formattedHour = (hour % 12 === 0 ? 12 : hour % 12) + (hour < 12 ? 'AM' : 'PM');

            return `${dailyDate.toLocaleDateString('default', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            })}, ${formattedHour}`;

        case "weekly":
            
            // ⬇️ FIXED: Show actual days in the selected week (e.g., Aug 18, Aug 19, ...)
            const weekDay = new Date(date);
            weekDay.setDate(date.getDate() + index);
            return weekDay.toLocaleDateString('default', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });

        case "monthly":
            // ⬇️ FIXED: Get the correct day of the month with full date
            const dayOfMonth = index + 1;
            const year = date.getFullYear();
            const month = date.getMonth();
            const newDate = new Date(year, month, dayOfMonth);
            return newDate.toLocaleDateString('default', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });

        case "yearly":
            // Existing fix for yearly — keep this
            const monthDate = new Date(date.getFullYear(), index);
            return monthDate.toLocaleDateString('default', {
                month: 'long',
                year: 'numeric'
            });

        default:
            return "";
    }
};


const StatisticGraphComponent = ({ selectedSalesManager }) => {
    const [interval, setInterval] = useState("monthly");
    const [activeIndex, setActiveIndex] = useState(2);
    const [months, setMonths] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [chartDatas, setChartDatas] = useState({});
    const scrollRef = useRef(null);
    const startDate = '2024-01-01';
    const [dataPeriod, setDataPeriod] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isDataInitialized, setIsDataInitialized] = useState(false);
    const [isChartUpdating, setIsChartUpdating] = useState(false);
    const apiUrl = config.apps.find((c) => c?.app_urls.includes(window.location.origin))?.api_url || 'abcd';
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);
    const options = ['Day', 'Week', 'Month', 'Year'];
    const [totalRevenue, setTotalRevenue] = useState(0);

    const { number } = useSpring({
        from: { number: 0 },
        number: totalRevenue,
        delay: 0,
        config: { mass: 1, tension: 120, friction: 14 }, // higher tension, lower friction for faster animation
    });

    const scroll = (direction) => {
        const container = scrollRef.current;
        if (container) {
            container.scrollBy({
                left: direction === 'left' ? -150 : 150,
                behavior: 'smooth',
            });
        }
    };

    const scrollToIndex = (index) => {
        const container = scrollRef.current;
        if (container) {
            const item = container.children[index];
            if (item) {
                const itemLeft = item.offsetLeft;
                const itemWidth = item.offsetWidth;
                const containerWidth = container.offsetWidth;
                const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);
                container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        const start = new Date(startDate);
        const end = new Date();
        let generated = [];

			const futureEndDate = new Date(end.setMonth(end.getMonth() + 2));

			switch (interval) {
            case "daily":
                generated = generateDays(start, end);
                break;
            case "weekly":
                generated = generateWeeks(start, end);
                break;
            case "monthly":
                generated = generateMonths(start, futureEndDate);
                break;
            case "yearly":
                generated = generateYears(start.getFullYear(), end.getFullYear());
                break;
            default:
                break;
        }

			const currentMonthIndex = generated.findLastIndex(month => !month.disabled);

			setMonths(generated);
			setSelectedIndex(currentMonthIndex > -1 ? currentMonthIndex : 0);

    }, [interval]);

    useEffect(() => {
        if (months.length > 0 && selectedIndex !== null) {
            scrollToIndex(selectedIndex);
            updateChartData(interval, months[selectedIndex]?.date);
            setDataPeriod(months[selectedIndex]?.label); 
            
        }
    }, [months, selectedIndex, selectedCurrency, selectedSalesManager]);

    const updateChartData = async (intervalType, baseDate = new Date()) => {

        setIsChartUpdating(true)
				let url = `${apiUrl}/api/dashboard/statistics/total-sales?interval=${intervalType}&date=${baseDate.toISOString()}`;
				if (selectedSalesManager) {
					url += `&sales_person_id=${selectedSalesManager}`;
				}

				const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        const defaultCurrency = selectedCurrency === 'all' ? 'usd' : selectedCurrency;
        const currencyInvoiceValueData = json.data?.invoice?.map(item => item[defaultCurrency] || 0);

        const calculateTotalRevenue = currencyInvoiceValueData.reduce((sum, val) => sum + val, 0);
        setTotalRevenue(calculateTotalRevenue);
        
        if (!isDataInitialized) {
            setIsDataInitialized(true);
            setIsLoading(false)
        }
        setIsChartUpdating(false)


        setChartDatas({
            options: {
                ...chartDatas.options, // retain styles
                chart: {
                    type: "area",
                    toolbar: { show: false },
                    zoom: { enabled: false },
                },
                stroke: {
                    curve: "smooth",
                    width: 2,
                },
                markers: {
                    size: 0,
                    colors: ["#3b82f6"],
                    strokeWidth: 0,
                    hover: { size: 4 },
                },
                // colors: ["#3b82f6"],
                colors: ["#3b82f6", "#10b981"],
                fill: {
                    type: "gradient",
                    gradient: {
                        shade: "light",
                        type: "vertical",
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0,
                        stops: [0, 100],
                    },
                },
                grid: { borderColor: "#f1f1f1" },
                xaxis: {
                    ...chartDatas.options?.xaxis,
                    categories: json.categories,
                    labels: {
                        style: { colors: "#6b7280", fontSize: "12px" },
                    },
                    tooltip: { enabled: false },
                    crosshairs: {
                        show: true,
                        stroke: { color: '#007bff', width: 2, dashArray: 0 }
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => `${getCurrencySymbolV3(defaultCurrency, 'eur')} ${val?.toLocaleString()}`,
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: () => "•",
                    style: {
                        colors: ["transparent"],
                        fontSize: "18px",
                    },
                    background: { enabled: false },
                },
                tooltip: {
                    shared: true,
                    intersect: false,

                    custom: function ({ series, dataPointIndex }) {
                        const label = formatDateLabel(
                        months[selectedIndex]?.date || new Date(),
                        dataPointIndex,
                        interval
                        );

                        return `
                            <div style="padding:10px; font-family:Arial; font-size:13px;">
                                <strong>${label}</strong><br/>
                                <div style="margin-top:5px;">
                                <div>Sales: ${getCurrencySymbolV3(defaultCurrency, 'eur')} ${series[0][dataPointIndex]?.toLocaleString()}</div>
                                </div>
                            </div>
                        `;
                    },
                    x: { show: true },
                },
            },
            series: [
                {
                    name: `Sales`,
                    data: json.data?.invoice?.map(item => item[defaultCurrency.toLowerCase()] || 0),
                },
            ],
        });
    };

    const handleIntervalChange = (label) => {
        switch (label) {
            case "Day": setInterval("daily"); break;
            case "Week": setInterval("weekly"); break;
            case "Month": setInterval("monthly"); break;
            case "Year": setInterval("yearly"); break;
            default: break;
        }
    };

    return (
        <>
            
            <div
                className="card"
                style={{
                    padding: '15px 15px'
                }}
            >
                <div className="mb-2 mt-5 r">
                    <div className={`text-lg fw-500`}>
                        Sales Statistics
                    </div>

                    <div className="row">
                        <div className="col-12">
                            {
                                isLoading ? (
                                    <div style={{ width: '320px', padding: '15px 0' }}>
                                        <Skeleton variant="rounded" width={'100%'} height={40} style={{ borderRadius: '32px' }} />
                                    </div>
                                ) : (
                                    <>
                                        {
                                            isChartUpdating && (
                                                <div style={{ position: 'relative' }}>

                                                    <div
                                                        className="dito"
                                                        style={{
                                                                width: '320px',
                                                                position: 'absolute',
                                                                border: '0px',
                                                                height: '44px',
                                                                background: 'rgba(224, 224, 224, 0.5)',
                                                                borderRadius: '32px',
                                                                left: '0px',
                                                                color: 'rgb(0, 0, 0)',
                                                                fontSize: '22px',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                gap: '5px',
                                                                backdropFilter: 'blur(8px)',
                                                                zIndex: '10',
                                                                top: '16px',
                                                        }}
                                                    >
                                                    </div>
                                                </div>
                                            )
                                        }
                                        <div className="segmented-control">
                                            <div
                                                className="slider"
                                                style={{ left: `${(100 / options.length) * activeIndex + 1}%`, width: `23%` }}
                                            />
                                            {options.map((option, index) => (
                                                <div
                                                    key={option}
                                                    className={`segment ${
                                                        interval === `${option.toLowerCase()}ly` ||
                                                        (option === 'Day' && interval === 'daily')
                                                            ? 'active'
                                                            : ''
                                                        }`}
                                                    onClick={() => {
                                                        handleIntervalChange(option)

                                                        setActiveIndex(index);
                                                    }}
                                                >
                                                    {option}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )
                            }
                        </div>
                    </div>
                </div>

                <div className="relative">
                    {
                        isLoading && (
                            <div style={{
                                width: '95%',
                                position: 'absolute',
                                marginTop: '2px',
                                zIndex: 1,
                                opacity: 1,
                                background: '#e0e0e0',
                                borderRadius: '32px'
                            }}>
                                <Skeleton variant="rounded" width={'100%'} height={45} style={{ borderRadius: '32px' }} />
                            </div>
                        )
                    }
                    
                {
                    isChartUpdating && (
                        <div style={{ position: 'relative' }}>
                            
                            <div
                                className="dito"
                                style={{
                                        width: '100%',
                                        position: 'absolute',
                                        border: '0px',
                                        height: '53px',
                                        background: 'rgba(224, 224, 224, 0.5)',
                                        borderRadius: '32px',
                                        left: '0px',
                                        color: 'rgb(0, 0, 0)',
                                        fontSize: '22px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '5px',
                                        backdropFilter: 'blur(8px)',
                                        zIndex: 10,
                                }}
                            >
                            </div>
                        </div>
                    )
                }
                <div
                    className="dashboard-date-slider flex overflow-x-auto scrollbar-hide"
                    style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#f8f8f8',
                            borderRadius: '9999px',
                            padding: '0px 10px',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                            marginBottom: '20px'
                    }}
                    
                >
                    <button
                        className=" left-0 h-full px-2 z-10"
                        onClick={() => scroll('left')}
                        style={{
                            border: '0',
                            background: 'none',
                            fontSize: '20px'
                        }}
                    >
                        &lt;
                    </button>
                    <div
                        className="flex overflow-x-auto scrollbar-hide py-2 gap-2"
                        ref={scrollRef}
                    >
                        {months.map((item, index) => (
                            <button
                                key={index}
                                    className={`dashboard-date-slider-date ${
                                        selectedIndex === index ? 'active' : ''
                                    } ${
                                        item.disabled ? 'disabled-month' : '' 
                                    }`}
                                onClick={() => {
                                    setSelectedIndex(index)
                                    setDataPeriod(item?.label)
                                }}
                            disabled={item.disabled}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <button
                        className=" right-0 h-full px-2 z-10"
                        onClick={() => scroll('right')}
                        style={{
                            border: '0',
                            background: 'none',
                            fontSize: '20px'
                        }}
                    >
                        &gt;
                    </button>
                </div>
                </div>
                {
                    isLoading ? (
                        <div style={{ width: '250px', paddingBottom: '5px' }}>
                            <Skeleton variant="rounded" width={'100%'} height={25} style={{ borderRadius: '32px' }} />
                        </div>
                    ) : (
                        <div className="flex gap-2 ml-2 align-items-center">
                            <div className="" style={{ fontSize: '14px' }}>
                                Total Revenue:
                            </div>
                            <div className="" style={{ fontSize: '19px', fontWeight: '600' }}>
                                {isChartUpdating ? (
                                    <div style={{ width: '150px', paddingBottom: '5px' }}>
                                        <Skeleton variant="rounded" width={'100%'} height={25} style={{ borderRadius: '32px' }} />
                                    </div>
                                ): (
                                    <div className="flex gap-1">
                                        <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-2px' : '0'}` }}>
                                            {getCurrencySymbolV2(selectedCurrency, 'usd', '16px', '16px')}        
                                        </div>
                                        <animated.div>
                                            {number.to((n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                                        </animated.div>
                                    </div>                                                
                                )}
                            </div>
                        </div>
                    )
                }
                {
                    isLoading ? (
                        <div style={{ width: '250px', paddingBottom: '5px', marginTop: '10px' }}>
                            <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                        </div>
                    ) : (
                        <div className="flex gap-2 ml-2">
                            <div className="text-gray-600">
                                Period: 
                            </div>
                            <div className="text-gray-600">
                                { dataPeriod }
                            </div>
                        </div>
                    )
                }
                {
                    isLoading ? (
                        <div style={{ width: '100%', marginTop: '20px', marginBottom: '0px' }}>
                            <Skeleton variant="rounded" width={'100%'} height={291} style={{ borderRadius: '18px' }} />
                        </div>
                    ) : (
                        <>
                            {
                                isChartUpdating && (
                                    <div style={{ position: 'relative' }}>

                                        <div
                                            className="dito"
                                            style={{
                                                width: '100%',
                                                position: 'absolute',
                                                border: '0px',
                                                height: '291px',
                                                background: 'rgba(224, 224, 224, 0.5)', // semi-transparent
                                                borderRadius: '18px',
                                                left: '0px',
                                                color: 'rgb(0, 0, 0)',
                                                fontSize: '22px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '5px',
                                                backdropFilter: 'blur(8px)', // <-- this adds the blur
                                                WebkitBackdropFilter: 'blur(8px)', // for Safari
                                                zIndex: 10,
                                            }}
                                        >
                                            <div style={{ color: 'rgb(0,0,0)', fontWeight: 500, zIndex: 11 }}>
                                                <FaSpinner className="fa-spin" /> Please wait...
                                            </div>
                                        </div>
                                    </div>
                                )
                            }
                            <div className="">
                                {chartDatas.options && (
                                    <Chart
                                        options={chartDatas.options}
                                        series={chartDatas.series}
                                        type="area"
                                        height={300}
                                    />
                                )}
                            </div>
                        </>
                    )
                }
            </div>
        </>
    );
};

export default StatisticGraphComponent;
